import React from 'react';
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  GridItem,
  HStack,
  Icon,
  IconButton,
  Image,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  VStack,
  useDisclosure,
  useColorModeValue,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  MdArrowBack,
  MdImage,
  MdOutlineOpenInFull,
  MdOutlineEdit,
  MdOutlineNotes,
  MdOutlineNumbers,
} from 'react-icons/md';
import { inspectionKeys } from 'hooks/useVehicleInspections';
import inspectionApi from 'services/inspection.api';
import EmptyState from 'components/ui/EmptyState';
import SectionHeader from 'components/ui/SectionHeader';
import Surface from 'components/ui/Surface';

const formatDate = (value) => (value ? new Date(value).toLocaleString() : '-');
const isVideoMedia = (media) =>
  Boolean(
    media?.mimeType?.startsWith('video/') ||
      /\.(mp4|webm|mov|m4v|quicktime)$/i.test(
        media?.originalFileName || media?.fileName || media?.url || '',
      ),
  );

function DetailStat({ label, value, accent }) {
  return (
    <Box
      p="18px"
      borderRadius="22px"
      bg={accent}
      border="1px solid"
      borderColor="rgba(162, 176, 210, 0.14)"
    >
      <Text color="secondaryGray.700" fontSize="sm" mb="6px">
        {label}
      </Text>
      <Text fontWeight="800" fontSize="lg">
        {value}
      </Text>
    </Box>
  );
}

export default function InspectionDetailView() {
  const { uuid } = useParams();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const statBg = useColorModeValue(
    'rgba(246, 248, 255, 0.88)',
    'rgba(21, 29, 53, 0.72)',
  );
  const bodyText = useColorModeValue('secondaryGray.800', 'white');
  const labelText = useColorModeValue('secondaryGray.700', 'secondaryGray.300');
  const imageCardBg = useColorModeValue(
    'rgba(255,255,255,0.56)',
    'rgba(255,255,255,0.06)',
  );
  const imageCardBorder = useColorModeValue(
    'rgba(162, 176, 210, 0.18)',
    'rgba(255,255,255,0.08)',
  );
  const [previewImage, setPreviewImage] = React.useState(null);

  const detailQuery = useQuery({
    queryKey: inspectionKeys.detail(uuid),
    queryFn: () => inspectionApi.getDetail(uuid),
    enabled: Boolean(uuid),
  });

  const inspection = detailQuery.data?.data;
  const mandatory =
    inspection?.images?.filter((image) => image.type !== 'extra') || [];
  const extra =
    inspection?.images?.filter((image) => image.type === 'extra') || [];

  const openPreview = (image, fallbackLabel) => {
    setPreviewImage({
      url: image.url,
      mimeType: image.mimeType,
      title:
        fallbackLabel ||
        image.originalFileName ||
        image.fileName ||
        'Inspection image',
    });
    onOpen();
  };

  const closePreview = () => {
    setPreviewImage(null);
    onClose();
  };

  return (
    <Box pt={{ base: '130px', md: '120px', sm: '120px' }}>
      <Skeleton isLoaded={!detailQuery.isLoading} borderRadius="24px">
        <Stack spacing="24px">
          <Surface variant="elevated" p={{ base: '22px', md: '28px' }}>
            <SectionHeader
              title={inspection?.inspectionNumber || 'Inspection Detail'}
              description="Review the image set, inspection notes, and VIN-linked history from a single record view."
              action={
                <Stack
                  direction={{ base: 'column', sm: 'row' }}
                  w={{ base: '100%', md: 'auto' }}
                >
                  <Button
                    as={RouterLink}
                    to="/admin/inspections"
                    variant="subtle"
                    leftIcon={<Icon as={MdArrowBack} />}
                  >
                    Back to List
                  </Button>
                  <Button
                    as={RouterLink}
                    to={`/admin/inspections/${uuid}/edit`}
                    variant="brand"
                    leftIcon={<Icon as={MdOutlineEdit} />}
                  >
                    Edit Inspection
                  </Button>
                </Stack>
              }
            />
            <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} gap="16px">
              <DetailStat
                label="VIN Number"
                value={inspection?.vinNumber || '-'}
                accent={statBg}
              />
              <DetailStat
                label="Created Date"
                value={formatDate(inspection?.createdAt)}
                accent={statBg}
              />
              <DetailStat
                label="Total Media"
                value={inspection?.images?.length || 0}
                accent={statBg}
              />
              <Box
                p="18px"
                borderRadius="22px"
                bg={statBg}
                border="1px solid"
                borderColor="rgba(162, 176, 210, 0.14)"
              >
                <Text color="secondaryGray.700" fontSize="sm" mb="6px">
                  Inspection Status
                </Text>
                <Badge
                  colorScheme="green"
                  borderRadius="full"
                  px="10px"
                  py="5px"
                >
                  Active
                </Badge>
              </Box>
            </SimpleGrid>
          </Surface>

          <Grid templateColumns={{ base: '1fr', xl: '1.1fr 0.9fr' }} gap="24px">
            <Surface p={{ base: '22px', md: '28px' }}>
              <SectionHeader
                title="Remarks"
                description="Inspection notes and observations captured during the record creation process."
              />
              <HStack align="flex-start" spacing="14px">
                <Flex
                  align="center"
                  justify="center"
                  w="44px"
                  h="44px"
                  borderRadius="16px"
                  bg={statBg}
                  flexShrink="0"
                >
                  <Icon as={MdOutlineNotes} color="brand.500" boxSize="20px" />
                </Flex>
                <Text color={bodyText} lineHeight="1.8">
                  {inspection?.remarks ||
                    'No remarks were added for this inspection.'}
                </Text>
              </HStack>
            </Surface>

            <Surface p={{ base: '22px', md: '28px' }}>
              <SectionHeader
                title="Record Summary"
                description="Quick context for opening related records or continuing admin review."
              />
              <VStack align="stretch" spacing="14px">
                <HStack justify="space-between">
                  <HStack spacing="12px">
                    <Icon as={MdOutlineNumbers} color="brand.500" />
                    <Text color={labelText}>Inspection number</Text>
                  </HStack>
                  <Text fontWeight="700">
                    {inspection?.inspectionNumber || '-'}
                  </Text>
                </HStack>
                <Divider />
                {/* <HStack justify='space-between'>
                  <HStack spacing='12px'>
                    <Icon as={MdOutlineHistory} color='brand.500' />
                    <Text color={labelText}>VIN history count</Text>
                  </HStack>
                  <Text fontWeight='700'>{history.length}</Text>
                </HStack> */}
              </VStack>
            </Surface>
          </Grid>
          <Surface p={{ base: '22px', md: '28px' }}>
            <SectionHeader
              title="Required Vehicle Images"
              description="The front, left, right, and rear views captured for this inspection."
            />
            {mandatory.length ? (
              <Grid
                templateColumns={{
                  base: '1fr',
                  md: 'repeat(2, 1fr)',
                  xl: 'repeat(4, 1fr)',
                }}
                gap="18px"
              >
                {mandatory.map((image) => (
                  <GridItem key={image.key || image.type}>
                    <Box
                      borderRadius="24px"
                      overflow="hidden"
                      border="1px solid"
                      borderColor={imageCardBorder}
                      bg={imageCardBg}
                    >
                      {isVideoMedia(image) ? (
                        <Box
                          as="video"
                          src={image.url}
                          controls
                          h="220px"
                          w="100%"
                          bg="black"
                          objectFit="cover"
                        />
                      ) : (
                        <Image
                          src={image.url}
                          alt={image.type}
                          objectFit="cover"
                          h="220px"
                          w="100%"
                        />
                      )}
                      <HStack p="14px" justify="space-between">
                        <Text fontWeight="700" textTransform="capitalize">
                          {image.type}
                        </Text>
                        <HStack spacing="8px">
                          <Icon as={MdImage} color="brand.500" />
                          <IconButton
                            aria-label={`View ${image.type} image`}
                            icon={<MdOutlineOpenInFull />}
                            size="sm"
                            variant="subtle"
                            onClick={() => openPreview(image, image.type)}
                          />
                        </HStack>
                      </HStack>
                    </Box>
                  </GridItem>
                ))}
              </Grid>
            ) : (
              <EmptyState
                title="No required images found"
                description="This inspection does not yet have any required vehicle photos attached."
              />
            )}
          </Surface>

          <Surface p={{ base: '22px', md: '28px' }}>
            <SectionHeader
              title="Additional Media"
              description="Optional supporting photos and videos uploaded alongside the main vehicle views."
            />
            {extra.length ? (
              <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} gap="18px">
                {extra.map((image) => (
                  <Box
                    key={image.key}
                    borderRadius="24px"
                    overflow="hidden"
                    border="1px solid"
                    borderColor={imageCardBorder}
                    bg={imageCardBg}
                  >
                    {isVideoMedia(image) ? (
                      <Box
                        as="video"
                        src={image.url}
                        controls
                        h="220px"
                        w="100%"
                        bg="black"
                        objectFit="cover"
                      />
                    ) : (
                      <Image
                        src={image.url}
                        alt="Additional vehicle"
                        objectFit="cover"
                        h="220px"
                        w="100%"
                      />
                    )}
                    <HStack p="14px" justify="space-between">
                      <Text
                        fontSize="sm"
                        color="secondaryGray.700"
                        noOfLines={1}
                      >
                        {image.originalFileName ||
                          image.fileName ||
                          (isVideoMedia(image)
                            ? 'Additional video'
                            : 'Additional image')}
                      </Text>
                      <IconButton
                        aria-label="View additional media"
                        icon={<MdOutlineOpenInFull />}
                        size="sm"
                        variant="subtle"
                        onClick={() =>
                          openPreview(
                            image,
                            image.originalFileName ||
                              image.fileName ||
                              (isVideoMedia(image)
                                ? 'Additional video'
                                : 'Additional image'),
                          )
                        }
                      />
                    </HStack>
                  </Box>
                ))}
              </SimpleGrid>
            ) : (
              <EmptyState
                title="No additional media"
                description="Only the required inspection views were uploaded for this record."
              />
            )}
          </Surface>
        </Stack>
      </Skeleton>

      <Modal isOpen={isOpen} onClose={closePreview} size="6xl" isCentered>
        <ModalOverlay bg="rgba(15, 23, 42, 0.72)" backdropFilter="blur(8px)" />
        <ModalContent
          bg="rgba(16, 23, 40, 0.96)"
          border="1px solid"
          borderColor="rgba(255,255,255,0.08)"
        >
          <ModalCloseButton color="white" />
          <ModalBody p={{ base: '18px', md: '24px' }}>
            <Stack spacing="14px">
              <Text color="white" fontWeight="800" fontSize="lg">
                {previewImage?.title}
              </Text>
              {previewImage?.url ? (
                isVideoMedia(previewImage) ? (
                  <Box
                    as="video"
                    src={previewImage.url}
                    controls
                    maxH="78vh"
                    w="100%"
                    borderRadius="18px"
                    bg="black"
                  />
                ) : (
                  <Image
                    src={previewImage.url}
                    alt={previewImage.title || 'Inspection image preview'}
                    maxH="78vh"
                    w="100%"
                    objectFit="contain"
                    borderRadius="18px"
                  />
                )
              ) : null}
            </Stack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}
